import {
  cloneElement,
  isValidElement,
  useId,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react'
import { useBranding } from '../brand/BrandingProvider'
import { LogoStacked } from '../brand/LogoStacked'
import { Icon, type IconName } from './icons'
import styles from './layout.module.css'
import { ArrangedTable } from './ArrangedTable'

/*
  Layout primitives (ADR 0019). Every screen is built from these so spacing, type and
  form layout are the same everywhere: a Page holds Panels; a Panel holds a FormGrid of
  Fields and a table; Actions hold the buttons. Utilities are Tailwind on the brand tokens
  (`gap-5` is --space-5).
*/

type DivProps = HTMLAttributes<HTMLDivElement>

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

export function Stack({
  className,
  tight,
  loose,
  ...rest
}: DivProps & { tight?: boolean; loose?: boolean }) {
  return (
    <div
      className={cx('flex flex-col', tight ? 'gap-2' : loose ? 'gap-6' : 'gap-4', className)}
      {...rest}
    />
  )
}

export function Row({ className, between, ...rest }: DivProps & { between?: boolean }) {
  return (
    <div
      className={cx('flex flex-wrap items-center gap-3', between && 'justify-between', className)}
      {...rest}
    />
  )
}

/** Cards in as many columns as fit: tiles on Home, sub-screens in Settings. */
export function Grid({ className, ...rest }: DivProps) {
  return (
    <div
      className={cx(
        'grid items-start gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',
        className,
      )}
      {...rest}
    />
  )
}

/**
 * Panels side by side on a wide screen, stacked on a narrow one. `aside` makes three
 * columns, for a narrow list beside the record it opens: give the record `lg:col-span-2`.
 */
export function Columns({ className, aside, ...rest }: DivProps & { aside?: boolean }) {
  return (
    <div
      className={cx(
        'grid items-start gap-5',
        aside ? 'lg:grid-cols-3' : 'xl:grid-cols-2',
        className,
      )}
      {...rest}
    />
  )
}

/**
 * A screen's title, a one-line summary under it ("120 head in 1 herd · 0 in withdrawal"), and
 * its main actions at the right (direction B, 2026-10-23). `eyebrow` is accepted and not shown:
 * the section row names where the page is, so a label over the title only repeated it.
 */
export function PageHeader({
  title,
  summary,
  actions,
}: {
  eyebrow?: string
  title: string
  summary?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="pageTitle">{title}</h1>
        {summary && <p className="pageSummary">{summary}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

/** A screen: its header, then its panels, full content width. */
export function Page({
  eyebrow,
  title,
  summary,
  actions,
  className,
  children,
  ...rest
}: DivProps & { eyebrow?: string; title: string; summary?: ReactNode; actions?: ReactNode }) {
  return (
    <div className={cx('flex flex-col gap-5', className)} {...rest}>
      <PageHeader eyebrow={eyebrow} title={title} summary={summary} actions={actions} />
      {children}
    </div>
  )
}

/**
 * A card with a title: one job on a screen (a list, a form, a summary). `title` is an h2 at
 * --text-xl; `actions` sit at the right of the title. A Panel is a container for container
 * queries, so a FormGrid inside it takes as many columns as the panel, not the window, allows.
 */
export function Panel({
  title,
  eyebrow,
  actions,
  className,
  children,
  ...rest
}: Omit<HTMLAttributes<HTMLElement>, 'title'> & {
  title?: ReactNode
  eyebrow?: string
  actions?: ReactNode
}) {
  return (
    <section className={cx('card @container flex min-w-0 flex-col gap-4', className)} {...rest}>
      {(title || eyebrow || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            {eyebrow && <div className="card__eyebrow">{eyebrow}</div>}
            {title && <h2>{title}</h2>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  )
}

/**
 * Choices shown as pills, one on at a time: the herds above the cattle list (direction B).
 * Each is a real button that says whether it is pressed.
 */
export function Pills<K extends string>({
  options,
  value,
  onChange,
  className,
  children,
}: {
  options: readonly (readonly [K, ReactNode])[]
  value: K
  onChange: (key: K) => void
  className?: string
  children?: ReactNode
}) {
  return (
    <div className={cx('flex flex-wrap items-center gap-2', className)}>
      {options.map(([key, label]) => (
        <button
          key={key}
          type="button"
          className="pill"
          aria-pressed={value === key}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
      {children}
    </div>
  )
}

/** A sub-heading inside a Panel ("New sale" under "Sales"). */
export function SubHeading({ children, className }: { children: ReactNode; className?: string }) {
  return <h3 className={cx('mt-2', className)}>{children}</h3>
}

/**
 * A form's fields in columns: one on a narrow panel, up to four on a wide one. Put Fields,
 * Checks and Actions in it; `wide` on a Field spans two columns, `full` the whole row.
 */
export function FormGrid({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { onSubmit?: never }) {
  return (
    <div
      className={cx(
        'grid items-start gap-x-4 gap-y-3 @md:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4',
        className,
      )}
      {...rest}
    />
  )
}

/**
 * A labelled control: the label above, a hint or an error below. The control keeps its own
 * `id` (tests and labels find it by that) or is given one.
 */
export function Field({
  label,
  hint,
  error,
  wide,
  full,
  className,
  children,
}: {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  wide?: boolean
  full?: boolean
  className?: string
  children: ReactElement<{ id?: string }>
}) {
  const generated = useId()
  const id = (isValidElement(children) && children.props.id) || generated
  return (
    <div
      className={cx(
        'flex min-w-0 flex-col gap-1',
        wide && '@md:col-span-2',
        full && 'col-span-full',
        className,
      )}
    >
      <label className="label mb-0" htmlFor={id}>
        {label}
      </label>
      {cloneElement(children, { id })}
      {error ? <p className="fieldError">{error}</p> : hint ? <p className="hint">{hint}</p> : null}
    </div>
  )
}

/** A checkbox (or radio) with its label beside it. */
export function Check({
  label,
  className,
  children,
}: {
  label: ReactNode
  className?: string
  children: ReactElement
}) {
  return (
    <label className={cx('flex items-center gap-2 py-2 text-sm font-semibold', className)}>
      {children}
      <span>{label}</span>
    </label>
  )
}

/**
 * A screen's sub-sections as a tab bar: words on a rule, the open one underlined (direction B,
 * 2026-10-23; before, tabs were outlined buttons and looked like every other action).
 */
export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
  className,
}: {
  tabs: readonly (readonly [K, string])[]
  value: K
  onChange: (key: K) => void
  className?: string
}) {
  return (
    <div className={cx('tabBar', className)} role="tablist">
      {tabs.map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={value === key}
          className="tabItem"
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

/**
 * A row of buttons. In a FormGrid it takes the whole row, buttons at the right, under the
 * fields (owner, 2026-10-18: "should be in the bottom right corner of its container").
 * `inline` keeps it in a grid cell beside the fields, its buttons level with their controls.
 */
export function Actions({ className, inline, ...rest }: DivProps & { inline?: boolean }) {
  return (
    <div
      className={cx(
        'flex flex-wrap items-center gap-2',
        inline ? 'self-end' : 'col-span-full justify-end',
        className,
      )}
      {...rest}
    />
  )
}

/** Key figures in a row: label above, value below. */
export function Kpis({
  items,
  className,
}: {
  items: { label: ReactNode; value: ReactNode; tone?: 'danger' | 'success' }[]
  className?: string
}) {
  return (
    <dl className={cx('m-0 grid grid-cols-2 gap-4 @xl:grid-cols-4', className)}>
      {items.map((item, i) => (
        <div key={i} className="flex min-w-0 flex-col">
          <dt className="text-sm text-muted">{item.label}</dt>
          <dd
            className={cx(
              'm-0 text-lg font-semibold',
              item.tone === 'danger' && 'text-danger-text',
              item.tone === 'success' && 'text-success-text',
            )}
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * A table that scrolls sideways inside its panel instead of widening the page. `empty` is
 * shown in its place when there are no rows ("No herds defined."), so an empty list reads as
 * a sentence, not a row of column headers (owner, 2026-10-18). Its table sorts by any column and,
 * when long enough, gets a search-and-dropdown toolbar above it (ArrangedTable; owner,
 * 2026-10-24 and 2026-10-27); `plain` is for a table that does its own.
 */
export function TableWrap({
  className,
  empty,
  plain,
  children,
  ...rest
}: DivProps & { empty?: ReactNode | false; plain?: boolean }) {
  const scroll = (content: ReactNode) => (
    <div className={cx('-mx-1 overflow-x-auto px-1', className)} {...rest}>
      {content}
    </div>
  )
  if (empty) return scroll(<Note>{empty}</Note>)
  if (!plain && isValidElement(children) && children.type === 'table') {
    return (
      <ArrangedTable table={children as ReactElement<{ children?: ReactNode }>} scroll={scroll} />
    )
  }
  return scroll(children)
}

/** A short line of explanation under a heading or a form. */
export function Note({ className, ...rest }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cx('m-0 text-sm text-muted', className)} {...rest} />
}

export function EmptyState({
  icon,
  title,
  children,
}: {
  icon: IconName
  title: string
  children?: ReactNode
}) {
  return (
    <div className={`card ${styles.empty}`} data-testid="empty-state">
      <div className={styles.emptyIcon}>
        <Icon name={icon} size={2.5} />
      </div>
      <h2 className={styles.emptyTitle}>{title}</h2>
      {children}
    </div>
  )
}

/**
 * A ranch screen opened with no ranch to work in: the person has no membership yet. Said
 * rather than a blank page (owner, 2026-10-17: "The UI does not permit me to enter cattle,
 * pastures, hay, leases"; the cause was an account with no membership).
 */
export function NoRanch() {
  return (
    <EmptyState icon="warning" title="No ranch to open yet">
      <p>
        Your sign-in has no ranch membership. Ask the ranch's owner, or a platform administrator in
        Settings, to add you; then reload.
      </p>
    </EmptyState>
  )
}

/**
 * A page shown with no shell: sign-in and the wrong-address notice. Stacked logo on cream,
 * the credit line in the footer (brief §2.6).
 */
export function Standalone({ children, testId }: { children: ReactNode; testId?: string }) {
  const branding = useBranding()
  return (
    <div className={`app-shell ${styles.standalone}`} data-testid={testId}>
      <LogoStacked productName={branding.productName} className={styles.standaloneLogo} />
      <div className={`card ${styles.standaloneCard}`}>{children}</div>
      <footer className={styles.standaloneFooter}>{branding.creditLine}</footer>
    </div>
  )
}
